import React, { useState, useEffect, Fragment } from 'react';
import { Task } from '../types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCirclePlay,
  faCirclePause,
  faStepForward,
  faStepBackward,
  faArrowRotateRight,
  faForward,
} from '@fortawesome/free-solid-svg-icons';
import { faClock } from '@fortawesome/free-regular-svg-icons';
import { Dialog, Transition, Switch } from "@headlessui/react";
import { TIMER_PRESETS, PresetType, HELP_TEXT } from '../constants/timerPresets';
import { usePostHog } from 'posthog-js/react';

interface TaskPlayerBarProps {
  todayTasks: Task[];
  currentTaskId: number | null;
  isTimerRunning: boolean;
  currentTime: number; // seconds elapsed
  totalTime: number; // total session time in seconds
  sessionType: 'work' | 'shortBreak';
  selectedPreset: PresetType;
  currentSession: number; // current work session number (1-based)
  completedSessions: number;
  totalCompletedPomodoros: number;
  soundSettings: {
    countdownBeeps: boolean;
    twoMinWarning: boolean;
  };
  isImmersive?: boolean;
  isMinimized?: boolean;
  onPlayPause: () => void;
  onSkipBreak: () => void;
  onNextTask: () => void;
  onPreviousTask: () => void;
  onRestartSession: () => void;
  onTaskSelect: (taskId: number) => void;
  onPresetChange: (preset: PresetType) => void;
  onSkipSession: () => void;
  onSkipTask: () => void;
  onSoundToggle: (setting: 'countdownBeeps' | 'twoMinWarning') => void;
}

const TaskPlayerBar: React.FC<TaskPlayerBarProps> = ({
  todayTasks,
  currentTaskId,
  isTimerRunning,
  currentTime,
  totalTime,
  sessionType,
  selectedPreset,
  currentSession,
  completedSessions,
  totalCompletedPomodoros,
  soundSettings,
  isImmersive = false,
  isMinimized = false,
  onPlayPause,
  onSkipBreak,
  onNextTask,
  onPreviousTask,
  onRestartSession,
  onTaskSelect,
  onPresetChange,
  onSkipSession,
  onSkipTask,
  onSoundToggle,
}) => {
  const posthog = usePostHog();
  const [isVisible, setIsVisible] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [wasTimerRunning, setWasTimerRunning] = useState(false);
  
  // Get current task
  const currentTask = todayTasks.find(task => task.id === currentTaskId) || todayTasks[0];
  const currentTaskIndex = currentTask ? todayTasks.findIndex(task => task.id === currentTask.id) : -1;
  
  // Check if we have tasks to show the bar
  useEffect(() => {
    setIsVisible(todayTasks.length > 0);
  }, [todayTasks]);

  // Track screen size for responsive modal
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Trigger mount animation for modal
  useEffect(() => {
    if (showHelp) {
      // Slight delay to ensure DOM is ready for transition
      const timer = setTimeout(() => setIsMounted(true), 10);
      return () => clearTimeout(timer);
    } else {
      setIsMounted(false);
    }
  }, [showHelp]);

  // Track timer started/paused/resumed
  useEffect(() => {
    if (isTimerRunning && !wasTimerRunning) {
      // Timer just started or resumed
      if (currentTime === 0) {
        // Timer started from beginning
        posthog?.capture('timer_started', {
          session_type: sessionType,
          preset: selectedPreset,
          session_number: currentSession
        });
      } else {
        // Timer resumed
        posthog?.capture('timer_resumed', {
          session_type: sessionType,
          time_remaining: totalTime - currentTime
        });
      }
    } else if (!isTimerRunning && wasTimerRunning) {
      // Timer just paused
      posthog?.capture('timer_paused', {
        session_type: sessionType,
        time_elapsed: currentTime,
        time_remaining: totalTime - currentTime
      });
    }
    setWasTimerRunning(isTimerRunning);
  }, [isTimerRunning, currentTime, sessionType, selectedPreset, currentSession, totalTime, wasTimerRunning, posthog]);

  // Track timer completed
  useEffect(() => {
    if (currentTime >= totalTime && totalTime > 0) {
      posthog?.capture('timer_completed', {
        session_type: sessionType,
        preset: selectedPreset,
        session_number: currentSession,
        total_sessions_completed: completedSessions
      });
    }
  }, [currentTime, totalTime, sessionType, selectedPreset, currentSession, completedSessions, posthog]);

  // Format time display
  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  // Calculate progress percentage
  const progressPercentage = totalTime > 0 ? (currentTime / totalTime) * 100 : 0;

  // Check button availability
  const hasPreviousTask = currentTaskIndex > 0;
  const hasNextTask = currentTaskIndex < todayTasks.length - 1;
  const isBreak = sessionType === 'shortBreak';

  if (!isVisible) return null;

  return (
    <div 
      className={`transform transition-transform duration-300 fixed bottom-0 ${
        isVisible ? 'translate-y-0' : 'translate-y-full'
      } ${isImmersive ? 'z-30' : ''}`}
      style={{
        left: '50%',
        transform: `translateX(-50%) ${isVisible ? 'translateY(0)' : 'translateY(100%)'}`,
        transition: 'all 0.3s ease-in-out',
        width: isMinimized ? '448px' : '100%',
      }}
    >
      <div 
        className={`shadow-lg border ${
                  isMinimized 
          ? 'bg-white/80 dark:bg-dark-card/80 backdrop-blur-lg border-white/20 dark:border-white/10' 
          : isImmersive 
          ? 'bg-white/80 dark:bg-dark-card/80 backdrop-blur-md border-white/20 dark:border-white/10 border-t' 
          : 'bg-white/80 dark:bg-dark-card/80 backdrop-blur-md border-white/20 dark:border-white/10 border-t'
        }`}
        style={{
          transition: 'border-radius 0.25s ease-in-out',
          borderRadius: isMinimized ? '9999px' : '0px',
        }}
      >
        <div className={`mx-auto w-full ${isMinimized ? 'max-w-md px-6 py-3' : 'max-w-lg px-4'}`}>
          {isMinimized ? (
            // Minimized Layout - Redesigned to match screenshot layout
            <div className="flex items-center space-x-4">
              {/* Play/Pause Button */}
              <button
                onClick={onPlayPause}
                className="flex-shrink-0 p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                title={isTimerRunning ? 'Pause' : 'Play'}
              >
                {isTimerRunning ? (
                  <FontAwesomeIcon icon={faCirclePause} className="h-6 w-6" />
                ) : (
                  <FontAwesomeIcon icon={faCirclePlay} className="h-6 w-6" />
                )}
              </button>

              {/* Task Info and Progress Section */}
              <div className="flex-1 min-w-0">
                {/* Task Title */}
                <h3 className="text-base font-medium text-gray-900 dark:text-white truncate mb-2">
                  {currentTask?.text ? 
                    (currentTask.text.length > 50 ? currentTask.text.substring(0, 50) + '...' : currentTask.text) 
                    : 'No task selected'
                  }
                </h3>
                
                {/* Progress Bar Row */}
                <div className="mb-2">
                  <div className="bg-gray-400 dark:bg-gray-400 rounded-full h-1 relative overflow-hidden">
                    <div
                      className="absolute top-0 left-0 h-full bg-green-400 dark:bg-green-400 transition-all duration-300"
                      style={{ width: `${progressPercentage}%` }}
                    />
                  </div>
                </div>
                
                {/* Time Labels and Session Dots Row */}
                <div className="flex items-center justify-between">
                  {/* Time Labels - Left */}
                  <div className="flex items-center space-x-1">
                    <span className="text-xs text-gray-500 dark:text-dark-secondary tabular-nums">
                      {formatTime(currentTime)}
                    </span>
                    <span className="text-xs text-gray-400 dark:text-gray-600">/</span>
                    <span className="text-xs text-gray-500 dark:text-dark-secondary tabular-nums">
                      {formatTime(totalTime)}
                    </span>
                  </div>
                  
                  {/* Session Dots - Right */}
                  <div className="flex space-x-0.5 items-center">
                    {Array.from({ length: TIMER_PRESETS[selectedPreset].totalSessions }, (_, index) => (
                      <div
                        key={index}
                        className={`w-2 h-2 rounded-full transition-all duration-200 ${
                          isBreak
                            ? 'bg-blue-200 dark:bg-blue-800 opacity-60'
                            : index < currentSession
                            ? 'bg-green-500 dark:bg-green-400'
                            : 'bg-gray-400 dark:bg-gray-400'
                        }`}
                      />
                    ))}
                  </div>
                </div>
              </div>

              {/* Navigation Controls */}
              <div className="flex items-center space-x-1 flex-shrink-0">
                <button
                  onClick={onPreviousTask}
                  disabled={!hasPreviousTask}
                  className={`p-1.5 transition-colors ${
                    hasPreviousTask
                      ? 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                      : 'text-gray-200 dark:text-gray-600 cursor-not-allowed'
                  }`}
                  title="Previous task"
                >
                  <FontAwesomeIcon icon={faStepBackward} className="h-4 w-4" />
                </button>
                <button
                  onClick={onNextTask}
                  disabled={!hasNextTask}
                  className={`p-1.5 transition-colors ${
                    hasNextTask
                      ? 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                      : 'text-gray-200 dark:text-gray-600 cursor-not-allowed'
                  }`}
                  title="Next task"
                >
                  <FontAwesomeIcon icon={faStepForward} className="h-4 w-4" />
                </button>
                <button
                  onClick={onSkipSession}
                  className="p-1.5 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                  title={sessionType === 'work' ? 'Skip to break' : 'Skip to work session'}
                >
                  <FontAwesomeIcon icon={faForward} className="h-4 w-4" />
                </button>
              </div>
            </div>
          ) : (
            // Normal Layout - Original vertical design
            <>
              {/* Task Info Section */}
              <div className="pt-3 pb-2">
                <div className="text-center">
                  <h3 className="text-base font-medium text-gray-900 dark:text-white">
                    {currentTask?.text ? 
                      (currentTask.text.length > 60 ? currentTask.text.substring(0, 60) + '...' : currentTask.text) 
                      : 'No task selected'
                    }
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-dark-secondary mt-1">
                    {isBreak ? 'Break Time' : 'Focus Session'}
                  </p>
                  {/* Session Stepper - Always show but with different styling for breaks */}
                  <div className="flex justify-center mt-2 min-h-[1rem]">
                    <div className="flex space-x-1 items-center">
                      {Array.from({ length: TIMER_PRESETS[selectedPreset].totalSessions }, (_, index) => (
                        <div
                          key={index}
                          className={`w-4 rounded-full transition-all duration-200 ${
                            isBreak
                              ? 'bg-blue-200 dark:bg-blue-800 h-0.5 opacity-60' // Break state styling
                              : index < currentSession
                              ? 'bg-green-500 dark:bg-green-400 h-1'
                              : 'bg-gray-400 dark:bg-gray-400 h-0.5'
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="pb-2">
                <div className="flex items-center space-x-2">
                  <span className="text-xs text-gray-500 dark:text-dark-secondary tabular-nums text-right" style={{ minWidth: '2.5rem' }}>
                    {formatTime(currentTime)}
                  </span>
                  <div className="flex-1 bg-gray-400 dark:bg-gray-400 rounded-full h-1.5 relative overflow-hidden">
                    <div
                      className="absolute top-0 left-0 h-full bg-green-400 dark:bg-green-400 transition-all duration-300"
                      style={{ width: `${progressPercentage}%` }}
                    />
                  </div>
                  <span className="text-xs text-gray-500 dark:text-dark-secondary tabular-nums text-left" style={{ minWidth: '3rem' }}>
                    -{formatTime(totalTime - currentTime)}
                  </span>
                </div>
              </div>

              {/* Control Buttons */}
              <div className="pb-3">
                <div className="relative">
                  <div className="flex items-center justify-center space-x-4">
                    {/* Restart Button */}
                    <button
                      onClick={() => {
                        posthog?.capture('timer_reset', {
                          session_type: sessionType,
                          time_elapsed: currentTime
                        });
                        onRestartSession();
                      }}
                      className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                      title="Restart session"
                    >
                      <FontAwesomeIcon icon={faArrowRotateRight} className="h-5 w-5" />
                    </button>

                    {/* Previous Task */}
                    <button
                      onClick={onPreviousTask}
                      disabled={!hasPreviousTask}
                      className={`p-2 transition-colors ${
                        hasPreviousTask
                          ? 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                          : 'text-gray-200 dark:text-gray-600 cursor-not-allowed'
                      }`}
                      title="Previous task"
                    >
                      <FontAwesomeIcon icon={faStepBackward} className="h-6 w-6" />
                    </button>

                    {/* Play/Pause Button */}
                    <button
                      onClick={onPlayPause}
                      className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                      title={isTimerRunning ? 'Pause' : 'Play'}
                    >
                      {isTimerRunning ? (
                        <FontAwesomeIcon icon={faCirclePause} className="h-8 w-8" />
                      ) : (
                        <FontAwesomeIcon icon={faCirclePlay} className="h-8 w-8" />
                      )}
                    </button>

                    {/* Next Task */}
                    <button
                      onClick={onNextTask}
                      disabled={!hasNextTask}
                      className={`p-2 transition-colors ${
                        hasNextTask
                          ? 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                          : 'text-gray-200 dark:text-gray-600 cursor-not-allowed'
                      }`}
                      title="Next task"
                    >
                      <FontAwesomeIcon icon={faStepForward} className="h-6 w-6" />
                    </button>

                    {/* Skip Session Button */}
                    <button
                      onClick={onSkipSession}
                      className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                      title={sessionType === 'work' ? 'Skip to break' : 'Skip to work session'}
                    >
                      <FontAwesomeIcon icon={faForward} className="h-5 w-5" />
                    </button>
                  </div>
                  
                  {/* Clock Icon - Bottom Right */}
                  <button
                    className="absolute right-0 top-1/2 transform -translate-y-1/2 p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                    onClick={() => setShowHelp(true)}
                    aria-label="Timer presets"
                  >
                    <FontAwesomeIcon icon={faClock} className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Timer Help Dialog */}
      <Transition appear show={showHelp} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          onClose={() => setShowHelp(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div 
              className="fixed inset-0 bg-black/25 dark:bg-black/50 transition-opacity duration-300" 
              aria-hidden="true"
              style={{ opacity: isMobile && isMounted ? 1 : undefined }}
            />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto sm:flex sm:items-center sm:justify-center sm:p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                className="w-full transform bg-white dark:bg-dark-card text-left align-middle shadow-xl transition-all flex flex-col
                           sm:max-w-2xl sm:rounded-2xl sm:p-6
                           max-sm:inset-x-0 max-sm:absolute max-sm:bottom-0 max-sm:rounded-t-2xl max-sm:border-t max-sm:border-gray-200 max-sm:dark:border-gray-700 max-sm:max-h-[80vh] max-sm:pb-safe"
                role="dialog"
                aria-modal="true"
                aria-labelledby="timer-help-dialog-title"
                style={{
                  transform: isMobile ? (isMounted ? 'translateY(0)' : 'translateY(100%)') : undefined,
                  transitionProperty: isMobile ? 'transform' : undefined,
                  transitionDuration: isMobile ? '300ms' : undefined,
                  transitionTimingFunction: isMobile ? 'ease-out' : undefined,
                }}
              >
                {/* Header - Fixed */}
                <div className="flex justify-between items-start flex-shrink-0 sm:mb-0 max-sm:p-6 max-sm:pb-4">
                  <Dialog.Title
                    as="h2"
                    id="timer-help-dialog-title"
                    className="text-lg font-medium leading-6 text-gray-900 dark:text-dark-primary"
                  >
                    Timer Presets
                  </Dialog.Title>
                  <button
                    type="button"
                    onClick={() => setShowHelp(false)}
                    className="text-gray-400 dark:text-dark-secondary hover:text-gray-500 dark:hover:text-dark-primary focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-lg p-1"
                    aria-label="Close help dialog"
                  >
                    <span aria-hidden="true" className="text-2xl">
                      ×
                    </span>
                  </button>
                </div>

                {/* Scrollable Content */}
                <div className="overflow-y-auto flex-1 sm:mt-4 max-sm:px-6 max-sm:pb-6">
                    <div className="space-y-4">
                      {Object.entries(TIMER_PRESETS).map(([key, preset]) => {
                        const isSelected = selectedPreset === key;
                        return (
                          <button
                            key={key}
                            onClick={() => {
                              onPresetChange(key as PresetType);
                            }}
                            className={`w-full text-left preset-info rounded-lg p-4 transition-all duration-200 focus:outline-none ${
                              isSelected
                                ? 'bg-blue-100 dark:bg-blue-900/40 border-2 border-blue-500 dark:border-blue-400'
                                : 'bg-gray-50 dark:bg-dark-hover hover:bg-blue-50 dark:hover:bg-blue-900/20 border-2 border-transparent'
                            }`}
                          >
                            <h4 className={`text-sm font-medium ${
                              isSelected 
                                ? 'text-blue-900 dark:text-blue-100' 
                                : 'text-gray-900 dark:text-dark-primary'
                            }`}>
                              {preset.name}
                            </h4>
                            <p className={`text-sm mt-1 ${
                              isSelected 
                                ? 'text-blue-700 dark:text-blue-200' 
                                : 'text-gray-500 dark:text-dark-secondary'
                            }`}>
                              {HELP_TEXT.presets[key as PresetType]}
                            </p>
                          </button>
                        );
                      })}
                    </div>

                    {/* Sound Settings Section */}
                    <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                      <h3 className="text-base font-medium text-gray-900 dark:text-dark-primary mb-4">
                        Sound Notifications
                      </h3>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-700 dark:text-dark-secondary">
                            Timer countdown beeps
                          </span>
                          <Switch
                            checked={soundSettings.countdownBeeps}
                            onChange={() => onSoundToggle("countdownBeeps")}
                            className={`${
                              soundSettings.countdownBeeps
                                ? "bg-blue-600"
                                : "bg-gray-200 dark:bg-gray-500"
                            } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none`}
                          >
                            <span
                              className={`${
                                soundSettings.countdownBeeps
                                  ? "translate-x-6"
                                  : "translate-x-1"
                              } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                            />
                          </Switch>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-700 dark:text-dark-secondary">
                            2-minute warning
                          </span>
                          <Switch
                            checked={soundSettings.twoMinWarning}
                            onChange={() => onSoundToggle("twoMinWarning")}
                            className={`${
                              soundSettings.twoMinWarning
                                ? "bg-blue-600"
                                : "bg-gray-200 dark:bg-gray-500"
                            } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none`}
                          >
                            <span
                              className={`${
                                soundSettings.twoMinWarning
                                  ? "translate-x-6"
                                  : "translate-x-1"
                              } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                            />
                          </Switch>
                        </div>
                      </div>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
        </Dialog>
      </Transition>
    </div>
  );
};

export default TaskPlayerBar; 